import React, {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { ButtonModal } from './components/ButtonModal';
import { AiOutlinePlus } from 'react-icons/ai';
import { ModalCadastroRapido } from './components/ModalCadastroRapido';
import { SessionStorageService } from './class/SessionStorageService';

export type CadastroRapidoProdutoHandle = {
  /** Abre o modal programaticamente */
  open: () => void;
  /** Fecha o modal programaticamente */
  close: () => void;
};

interface CadRapidoProps {
  codTela: number;
  firstModalTitle?: string;
  buttonText?: string;
  onSuccess?: (novoProduto: any) => void;
  /** Quando false, não renderiza o botão interno de disparo (só o modal). */
  showTrigger?: boolean;
}

/**
 * Componente de cadastro rápido de produto.
 * - Expõe `open()` e `close()` via ref para que o pai possa controlar o modal.
 * - Evita acoplamento de clique ao componente pai.
 */
export const CadastroRapidoProduto = forwardRef<
  CadastroRapidoProdutoHandle,
  CadRapidoProps
>(
  (
    {
      codTela,
      firstModalTitle = 'Cadastro de Produto',
      buttonText = 'Cad. Simplificado',
      onSuccess,
      showTrigger = true,
    },
    ref,
  ) => {
    const [isCadastroRapidoOpen, setIsCadastroRapidoOpen] = useState(false);

    const abrirModal = useCallback(() => {
      setIsCadastroRapidoOpen(true);
      SessionStorageService.deleteProduto('produto-clonado', codTela);
    }, [codTela]);

    const onHideCadastroRapido = useCallback(() => {
      setIsCadastroRapidoOpen(false);
      SessionStorageService.deleteProduto('produto-clonado', codTela);
      sessionStorage.removeItem(`ncm-${codTela}`);
    }, [codTela]);

    // Expor API imperativa para o pai
    useImperativeHandle(
      ref,
      () => ({
        open: abrirModal,
        close: onHideCadastroRapido,
      }),
      [abrirModal, onHideCadastroRapido],
    );

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (codTela === 283 && event.key === 'F2') {
          event.preventDefault();
          abrirModal();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [codTela, abrirModal]);

    return (
      <>
        {showTrigger && (
          <ButtonModal
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              abrirModal();
            }}
          >
            <AiOutlinePlus />
            {buttonText}
          </ButtonModal>
        )}

        <ModalCadastroRapido
          codTela={codTela}
          firstModalTitle={firstModalTitle}
          /** Garanta que o prop de visibilidade reflita o estado local */
          isShow={isCadastroRapidoOpen}
          onHide={onHideCadastroRapido}
          setIsCadastroRapidoOpen={setIsCadastroRapidoOpen}
          isCadastroRapidoOpen={isCadastroRapidoOpen}
          onSuccess={onSuccess}
        />
      </>
    );
  },
);

CadastroRapidoProduto.displayName = 'CadastroRapidoProduto';
